import React, { Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { SpeciesLifecycleProvider } from './components/speciesLifecycle';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import { usePermissions } from './hooks/usePermissions';
import { WORKER_MODE_PERMISSIONS } from './components/worker/workerShared';
import OwnerOnly from './components/OwnerOnly';
import PublicRoute from './components/PublicRoute';
import ScrollToTop from './components/ScrollToTop';
import Layout from './components/Layout';
import AppInitializer from './components/AppInitializer';
import OfflineIndicator from './components/OfflineIndicator';
import OfflineSyncManager from './components/OfflineSyncManager';
import lazyWithRetry from './utils/lazyWithRetry';
// import Roles from './pages/Roles'; // Roles menu commented out
import { getMaveshiTheme } from './theme/maveshiTheme';
import './App.css';
import './styles/global-density.css';
import './styles/maveshi-shell.css';
import './styles/compact-theme.css';
import './styles/sidebar.css';
import './styles/maveshi-theme-overrides.css';
import './styles/header.css';
import './styles/global-search.css';
import './styles/advanced-animal-search.css';
import './styles/table.css';
import './styles/admin-list-pages.css';
import './styles/maveshi-stat-cards-global.css';

// Pages load on first visit (keeps the first load small on mobile connections)
const Login = lazyWithRetry(() => import('./pages/Login'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const Register = lazyWithRetry(() => import('./pages/Register'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const ChangePassword = lazyWithRetry(() => import('./pages/ChangePassword'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Invoices = lazyWithRetry(() => import('./pages/Invoices'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const ExecutiveDashboard = lazyWithRetry(() => import('./pages/ExecutiveDashboard'));
const Weather = lazyWithRetry(() => import('./pages/Weather'));
const THIForecast = lazyWithRetry(() => import('./pages/THIForecast'));
const Tasks = lazyWithRetry(() => import('./pages/Tasks'));
const Schedule = lazyWithRetry(() => import('./pages/Schedule'));
const AllSchedules = lazyWithRetry(() => import('./pages/AllSchedules'));
const TeamUsers = lazyWithRetry(() => import('./pages/TeamUsers'));
const Roster = lazyWithRetry(() => import('./pages/Roster'));
const Payroll = lazyWithRetry(() => import('./pages/Payroll'));
const Visitors = lazyWithRetry(() => import('./pages/Visitors'));
const AdminUsers = lazyWithRetry(() => import('./pages/AdminUsers'));
const Packages = lazyWithRetry(() => import('./pages/Packages'));
const Subscriptions = lazyWithRetry(() => import('./pages/Subscriptions'));
const Payments = lazyWithRetry(() => import('./pages/Payments'));
const PackageUpgrade = lazyWithRetry(() => import('./pages/PackageUpgrade'));
const UpgradeRequests = lazyWithRetry(() => import('./pages/UpgradeRequests'));
const Animals = lazyWithRetry(() => import('./pages/Animals'));
const BulkUploadAnimals = lazyWithRetry(() => import('./pages/BulkUploadAnimals'));
const BulkUploadTreatments = lazyWithRetry(() => import('./pages/BulkUploadTreatments'));
const BulkUploadFeedings = lazyWithRetry(() => import('./pages/BulkUploadFeedings'));
const BulkUploadInputs = lazyWithRetry(() => import('./pages/BulkUploadInputs'));
const BulkUploadMeasurements = lazyWithRetry(() => import('./pages/BulkUploadMeasurements'));
const BulkUploadMovements = lazyWithRetry(() => import('./pages/BulkUploadMovements'));
const BulkUploadYields = lazyWithRetry(() => import('./pages/BulkUploadYields'));
const RealTimeMilking = lazyWithRetry(() => import('./pages/RealTimeMilking'));
const WorkerMode = lazyWithRetry(() => import('./pages/WorkerMode'));
const WorkerSignIn = lazyWithRetry(() => import('./pages/WorkerSignIn'));
const WorkerLinkQr = lazyWithRetry(() => import('./pages/WorkerLinkQr'));
const BulkUploadGrazing = lazyWithRetry(() => import('./pages/BulkUploadGrazing'));
const AnimalGroups = lazyWithRetry(() => import('./pages/AnimalGroups'));
const FeedlotAnimals = lazyWithRetry(() => import('./pages/FeedlotAnimals'));
const FeedlotDashboard = lazyWithRetry(() => import('./pages/FeedlotDashboard'));
const Reports = lazyWithRetry(() => import('./pages/Reports'));
const AddEditAnimal = lazyWithRetry(() => import('./pages/AddEditAnimal'));
const Contacts = lazyWithRetry(() => import('./pages/Contacts'));
const AddEditContacts = lazyWithRetry(() => import('./pages/AddEditContacts'));
const PreviewAnimal = lazyWithRetry(() => import('./pages/PreviewAnimal'));
const AnimalTimeline = lazyWithRetry(() => import('./pages/AnimalTimeline'));
const FullSummaryAnimal = lazyWithRetry(() => import('./pages/FullSummaryAnimal'));
const PreviewAnimalGroup = lazyWithRetry(() => import('./pages/PreviewAnimalGroup'));
const AddEditFarm = lazyWithRetry(() => import('./pages/AddEditFarm'));
const AddEditTeamUser = lazyWithRetry(() => import('./pages/AddEditTeamUser'));
const AddEditAnimalGroup = lazyWithRetry(() => import('./pages/AddEditAnimalGroup'));
const Departments = lazyWithRetry(() => import('./pages/Departments'));
const AddEditDepartment = lazyWithRetry(() => import('./pages/AddEditDepartment'));
const CostCentres = lazyWithRetry(() => import('./pages/CostCentres'));
const AddEditCostCentre = lazyWithRetry(() => import('./pages/AddEditCostCentre'));
const AddEditZone = lazyWithRetry(() => import('./pages/AddEditZone'));
const AddEditShed = lazyWithRetry(() => import('./pages/AddEditShed'));
const AddEditLot = lazyWithRetry(() => import('./pages/AddEditLot'));
const AddEditPen = lazyWithRetry(() => import('./pages/AddEditPen'));
const FarmLocations = lazyWithRetry(() => import('./pages/FarmLocations'));
const FarmsLocationsDashboard = lazyWithRetry(() => import('./pages/FarmsLocationsDashboard'));
const FarmMap = lazyWithRetry(() => import('./pages/FarmMap'));
const ChartOfAccounts = lazyWithRetry(() => import('./pages/ChartOfAccounts'));
const AddEditChartOfAccount = lazyWithRetry(() => import('./pages/AddEditChartOfAccount'));
const AccountingDashboard = lazyWithRetry(() => import('./pages/AccountingDashboard'));
const FinanceDashboard = lazyWithRetry(() => import('./pages/finance/FinanceDashboard'));
const GroupProfitLoss = lazyWithRetry(() => import('./pages/finance/GroupProfitLoss'));
const MilkMargin = lazyWithRetry(() => import('./pages/finance/MilkMargin'));
const BudgetVsActual = lazyWithRetry(() => import('./pages/finance/BudgetVsActual'));
const OverheadAllocation = lazyWithRetry(() => import('./pages/finance/OverheadAllocation'));
const ComparePeriods = lazyWithRetry(() => import('./pages/finance/ComparePeriods'));
const FeedlotEconomics = lazyWithRetry(() => import('./pages/finance/FeedlotEconomics'));
const BreedingCosts = lazyWithRetry(() => import('./pages/finance/BreedingCosts'));
const HealthCosts = lazyWithRetry(() => import('./pages/finance/HealthCosts'));
const PastureCosts = lazyWithRetry(() => import('./pages/finance/PastureCosts'));
const LifecycleCost = lazyWithRetry(() => import('./pages/finance/LifecycleCost'));
const OtherCharges = lazyWithRetry(() => import('./pages/OtherCharges'));
const AccountingLedgers = lazyWithRetry(() => import('./pages/AccountingLedgers'));
const Sales = lazyWithRetry(() => import('./pages/Sales'));
const FeedFormulations = lazyWithRetry(() => import('./pages/FeedFormulations'));
const AddEditFeedFormulation = lazyWithRetry(() => import('./pages/AddEditFeedFormulation'));
const Inventory = lazyWithRetry(() => import('./pages/Inventory'));
const InventoryDashboard = lazyWithRetry(() => import('./pages/InventoryDashboard'));
const InventoryReceiveStock = lazyWithRetry(() => import('./pages/InventoryReceiveStock'));
const InventoryStockBatches = lazyWithRetry(() => import('./pages/InventoryStockBatches'));
const InventoryUsageLog = lazyWithRetry(() => import('./pages/InventoryUsageLog'));
const Warehouses = lazyWithRetry(() => import('./pages/Warehouses'));
const WarehouseDetail = lazyWithRetry(() => import('./pages/WarehouseDetail'));
const AddEditInventory = lazyWithRetry(() => import('./pages/AddEditInventory'));
const Activities = lazyWithRetry(() => import('./pages/Activities'));
const MaintenanceDashboard = lazyWithRetry(() => import('./pages/MaintenanceDashboard'));
const Equipment = lazyWithRetry(() => import('./pages/Equipment'));
const MaintenanceSchedules = lazyWithRetry(() => import('./pages/MaintenanceSchedules'));
const MilkingOverview = lazyWithRetry(() => import('./pages/MilkingOverview'));
const MilkTanksPage = lazyWithRetry(() => import('./pages/milkInventory/MilkTanksPage'));
const MilkRoutingPage = lazyWithRetry(() => import('./pages/milkInventory/MilkRoutingPage'));
const MilkBalancePage = lazyWithRetry(() => import('./pages/milkInventory/MilkBalancePage'));
const MilkDispositionsPage = lazyWithRetry(() => import('./pages/milkInventory/MilkDispositionsPage'));
const MilkBuyersPage = lazyWithRetry(() => import('./pages/milkInventory/MilkBuyersPage'));
const MilkCollectionsPage = lazyWithRetry(() => import('./pages/milkInventory/MilkCollectionsPage'));
const MilkQualityPage = lazyWithRetry(() => import('./pages/milkInventory/MilkQualityPage'));
const MilkTankLogsPage = lazyWithRetry(() => import('./pages/milkInventory/MilkTankLogsPage'));
const MilkWorkersPage = lazyWithRetry(() => import('./pages/milkInventory/MilkWorkersPage'));
const MilkCalvesPage = lazyWithRetry(() => import('./pages/milkInventory/MilkCalvesPage'));
const MilkProcessingPage = lazyWithRetry(() => import('./pages/milkInventory/MilkProcessingPage'));
const MilkWastagePage = lazyWithRetry(() => import('./pages/milkInventory/MilkWastagePage'));
const MeasurementsDashboard = lazyWithRetry(() => import('./pages/MeasurementsDashboard'));
const BreederDashboard = lazyWithRetry(() => import('./pages/BreederDashboard'));
const SemenCatalog = lazyWithRetry(() => import('./pages/SemenCatalog'));
const GeneticsPerformance = lazyWithRetry(() => import('./pages/GeneticsPerformance'));
const BreedingProgrammes = lazyWithRetry(() => import('./pages/BreedingProgrammes'));
const MaleEvaluation = lazyWithRetry(() => import('./pages/MaleEvaluation'));
const ProgenyTracing = lazyWithRetry(() => import('./pages/ProgenyTracing'));
const FeedLibrary = lazyWithRetry(() => import('./pages/FeedLibrary'));
const FeedHub = lazyWithRetry(() => import('./pages/FeedHub'));
const FeedBunk = lazyWithRetry(() => import('./pages/FeedBunk'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications'));
const LeastCostRation = lazyWithRetry(() => import('./pages/LeastCostRation'));
const SireDirectory = lazyWithRetry(() => import('./pages/SireDirectory'));
const GeneticsOverview = lazyWithRetry(() => import('./pages/GeneticsOverview'));
const FindBreedingPairs = lazyWithRetry(() => import('./pages/FindBreedingPairs'));
const AnimalGenetics = lazyWithRetry(() => import('./pages/AnimalGenetics'));
const RecordMating = lazyWithRetry(() => import('./pages/RecordMating'));
const BirthsAndLosses = lazyWithRetry(() => import('./pages/BirthsAndLosses'));
const FeedForecast = lazyWithRetry(() => import('./pages/FeedForecast'));
const FeedStores = lazyWithRetry(() => import('./pages/FeedStores'));
const Feeders = lazyWithRetry(() => import('./pages/Feeders'));
const FeedEfficiency = lazyWithRetry(() => import('./pages/FeedEfficiency'));
const NitrogenTanks = lazyWithRetry(() => import('./pages/NitrogenTanks'));
const GestationCalendar = lazyWithRetry(() => import('./pages/GestationCalendar'));
const TreatmentRegistry = lazyWithRetry(() => import('./pages/TreatmentRegistry'));
const FeedPlans = lazyWithRetry(() => import('./pages/FeedPlans'));
const FeedPlanForm = lazyWithRetry(() => import('./pages/FeedPlanForm'));
const MovementDashboard = lazyWithRetry(() => import('./pages/MovementDashboard'));
const MovementRegistry = lazyWithRetry(() => import('./pages/MovementRegistry'));
const FeedingRegistry = lazyWithRetry(() => import('./pages/FeedingRegistry'));
const MeasurementRegistry = lazyWithRetry(() => import('./pages/MeasurementRegistry'));
const BreedingRegistry = lazyWithRetry(() => import('./pages/BreedingRegistry'));
const StatusRegistry = lazyWithRetry(() => import('./pages/StatusRegistry'));
const StatusDashboard = lazyWithRetry(() => import('./pages/StatusDashboard'));
const AnimalDashboard = lazyWithRetry(() => import('./pages/AnimalDashboard'));
const OffspringRegistry = lazyWithRetry(() => import('./pages/OffspringRegistry'));
const SiblingRegistry = lazyWithRetry(() => import('./pages/SiblingRegistry'));
const GenealogyRegistry = lazyWithRetry(() => import('./pages/GenealogyRegistry'));
const InputRegistry = lazyWithRetry(() => import('./pages/InputRegistry'));
const NoteRegistry = lazyWithRetry(() => import('./pages/NoteRegistry'));
const TaggingDashboard = lazyWithRetry(() => import('./pages/tagging/TaggingDashboard'));
const TaggingSystemTags = lazyWithRetry(() => import('./pages/tagging/TaggingSystemTags'));
const TaggingCustomTags = lazyWithRetry(() => import('./pages/tagging/TaggingCustomTags'));
const TaggingSmartTags = lazyWithRetry(() => import('./pages/tagging/TaggingSmartTags'));
const TaggingCohortTags = lazyWithRetry(() => import('./pages/tagging/TaggingCohortTags'));
const TaggingPerformanceTags = lazyWithRetry(() => import('./pages/tagging/TaggingPerformanceTags'));
const TaggingRulesConfig = lazyWithRetry(() => import('./pages/tagging/TaggingRulesConfig'));
const ScheduleDashboard = lazyWithRetry(() => import('./pages/ScheduleDashboard'));
const DailyOperations = lazyWithRetry(() => import('./pages/DailyOperations'));
const SyncProtocolTemplates = lazyWithRetry(() => import('./pages/SyncProtocolTemplates'));
const Synchronization = lazyWithRetry(() => import('./pages/Synchronization'));
const GrazingManager = lazyWithRetry(() => import('./pages/GrazingManager'));
const Drafting = lazyWithRetry(() => import('./pages/Drafting'));
const HealthTreatment = lazyWithRetry(() => import('./pages/HealthTreatment'));
const WastageDashboard = lazyWithRetry(() => import('./pages/WastageDashboard'));
const StaffTeamsDashboard = lazyWithRetry(() => import('./pages/StaffTeamsDashboard'));
const SecurityBiosecurityDashboard = lazyWithRetry(() => import('./pages/SecurityBiosecurityDashboard'));
const AlertInbox = lazyWithRetry(() => import('./pages/AlertInbox'));
const AlertDetail = lazyWithRetry(() => import('./pages/AlertDetail'));
const PublicFieldReport = lazyWithRetry(() => import('./pages/PublicFieldReport'));
const SubmitFieldReport = lazyWithRetry(() => import('./pages/SubmitFieldReport'));
const MyFieldReports = lazyWithRetry(() => import('./pages/MyFieldReports'));
const FieldReportQr = lazyWithRetry(() => import('./pages/FieldReportQr'));
const THIReference = lazyWithRetry(() => import('./pages/THIReference'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
queryClient.setQueryDefaults(['milk-inventory'], {
  staleTime: 2 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
});
// Treatment pages: show cached lists on a revisit instead of loading again; saves, doses and
// return to vat invalidate these keys, so changes still show straight away.
queryClient.setQueryDefaults(['treatment-health'], {
  staleTime: 60 * 1000,
  gcTime: 10 * 60 * 1000,
});
queryClient.setQueryDefaults(['treatment-registry'], {
  staleTime: 60 * 1000,
  gcTime: 10 * 60 * 1000,
});

// Worker-mode members start on the big-button screen; its "Full app" button stops this for the tab
const WorkerModeRedirect = ({ children }) => {
  const { user } = useAuth();
  const { canAny } = usePermissions();
  let openedFullApp = false;
  try {
    openedFullApp = sessionStorage.getItem('worker_full_app') === String(user?.id);
  } catch {
    // no session storage: keep redirecting
  }
  if (user?.worker_mode && !openedFullApp && canAny(WORKER_MODE_PERMISSIONS)) return <Navigate to="/worker" replace />;
  return children;
};

function AppContent() {
  const { isCompact, themeMode, colorScheme, isMobileComfort } = useTheme();
  const { isRtl } = useLanguage();

  const maveshiTheme = useMemo(
    () => getMaveshiTheme(themeMode, isCompact, colorScheme, isMobileComfort),
    [themeMode, isCompact, colorScheme, isMobileComfort]
  );

  return (
    <ConfigProvider
      direction={isRtl ? 'rtl' : 'ltr'}
      theme={maveshiTheme}
    >
      <Router>
        <OfflineIndicator />
        <OfflineSyncManager />
        <ScrollToTop />
        <AppInitializer>
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '96px 0' }}><Spin /></div>}>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Notifications />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <WorkerModeRedirect>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </WorkerModeRedirect>
                </ProtectedRoute>
              }
            />
            <Route
              path="/worker"
              element={
                <ProtectedRoute permissions={WORKER_MODE_PERMISSIONS}>
                  <WorkerMode />
                </ProtectedRoute>
              }
            />
            <Route
              path="/executive-dashboard"
              element={
                <ProtectedRoute permission="view reports">
                  <Layout>
                    <ExecutiveDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/weather"
              element={
                <ProtectedRoute permission="view weather">
                  <Layout>
                    <Weather />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hli-forecast"
              element={
                <ProtectedRoute permission="view weather">
                  <Layout>
                    <THIForecast />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <Tasks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <Schedule />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Feeding is planned in the Feed planner. */}
            <Route path="/feeding-schedule" element={<Navigate to="/feed/plans" replace />} />
            <Route
              path="/daily-operations"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <DailyOperations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule-dashboard"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <ScheduleDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedules/all"
              element={
                <ProtectedRoute permission="view dashboard">
                  <Layout>
                    <AllSchedules />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/roster"
              element={
                <ProtectedRoute permission="view roster">
                  <Layout>
                    <Roster />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <ProtectedRoute permission="view payroll">
                  <Layout>
                    <Payroll />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitors"
              element={
                <ProtectedRoute permission="view visitors">
                  <Layout>
                    <Visitors />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff-teams-dashboard"
              element={
                <ProtectedRoute permission="view users">
                  <Layout>
                    <StaffTeamsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Maintenance Module Routes */}
            <Route
              path="/maintenance"
              element={
                <ProtectedRoute permission="view maintenance">
                  <Layout>
                    <MaintenanceDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance/equipment"
              element={
                <ProtectedRoute permission="view maintenance">
                  <Layout>
                    <Equipment />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance/schedules"
              element={
                <ProtectedRoute permission="view maintenance">
                  <Layout>
                    <MaintenanceSchedules />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Milking Dashboard Route */}
            <Route
              path="/milking"
              element={
                <ProtectedRoute permissions={['view milk inventory', 'view animal yields']}>
                  <Layout>
                    <MilkingOverview />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/inventory"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <Navigate to="/milking?tab=balance" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/tanks"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkTanksPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/routing"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkRoutingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/balance"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkBalancePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/dispositions"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkDispositionsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/buyers"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkBuyersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/collections"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkCollectionsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/quality"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkQualityPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/tank-logs"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkTankLogsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/workers"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkWorkersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/calves"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkCalvesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/processing"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkProcessingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/milking/wastage"
              element={
                <ProtectedRoute permission="view milk inventory">
                  <Layout>
                    <MilkWastagePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurements"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <MeasurementsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurements/registry"
              element={
                <ProtectedRoute permission="view animal measurements">
                  <Layout>
                    <MeasurementRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/calendar"
              element={
                <ProtectedRoute permissions={['view animal breeding', 'view reports']}>
                  <Layout>
                    <GestationCalendar />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/sync-templates" element={<Navigate to="/synchronization/templates" replace />} />
            <Route
              path="/synchronization/templates"
              element={
                <ProtectedRoute permissions={['view animal breeding', 'view animals']}>
                  <Layout>
                    <SyncProtocolTemplates />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/synchronization"
              element={
                <ProtectedRoute permissions={['view animal breeding', 'view animals']}>
                  <Layout>
                    <Synchronization />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/synchronization/program/:animalId/:programId"
              element={
                <ProtectedRoute permissions={['view animal breeding', 'view animals']}>
                  <Layout>
                    <Synchronization />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/synchronization/:view"
              element={
                <ProtectedRoute permissions={['view animal breeding', 'view animals']}>
                  <Layout>
                    <Synchronization />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <BreederDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/semen"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <SemenCatalog />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/population"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <GeneticsOverview key="overview" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/population-search"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <GeneticsOverview key="search" view="search" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/animal"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <AnimalGenetics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/record"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <RecordMating />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/outcomes"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <BirthsAndLosses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/pairs"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <FindBreedingPairs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/programmes"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <BreedingProgrammes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/sires"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <SireDirectory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/progeny"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <ProgenyTracing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/male-evaluation"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <MaleEvaluation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/nitrogen-tanks"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <NitrogenTanks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/genetics"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <GeneticsPerformance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/breeding/registry"
              element={
                <ProtectedRoute permission="view animal breeding">
                  <Layout>
                    <BreedingRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Health & Treatment module; the old treatment pages moved into it */}
            <Route path="/treatments" element={<Navigate to="/health" replace />} />
            <Route path="/treatments/registry" element={<Navigate to="/health/records" replace />} />
            <Route
              path="/health/records"
              element={
                <ProtectedRoute permission="view animal treatments">
                  <Layout>
                    <TreatmentRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/health/trends" element={<Navigate to="/health" replace />} />
            <Route
              path="/health/add"
              element={
                <ProtectedRoute permission="create animal treatments">
                  <Layout>
                    <HealthTreatment view="add" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {['/health', '/health/:view', '/health/animals/:animalId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute permission="view animal treatments">
                    <Layout>
                      <HealthTreatment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/feed"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/daily"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/efficiency"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedEfficiency />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/library"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedLibrary />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/least-cost"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <LeastCostRation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/bunk"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedBunk />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/forecast"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedForecast />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/stores/:slug"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedStores />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/feeders"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <Feeders />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/feed/rations" element={<Navigate to="/feed/plans" replace />} />
            <Route
              path="/feed/plans"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedPlans />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/plans/new"
              element={
                <ProtectedRoute permission="create animal feedings">
                  <Layout>
                    <FeedPlanForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed/plans/:id/edit"
              element={
                <ProtectedRoute permission="edit animal feedings">
                  <Layout>
                    <FeedPlanForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feeding"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feeding/registry"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedingRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wastage"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <WastageDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/movement"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <MovementDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/movement/registry"
              element={
                <ProtectedRoute permission="view animal movements">
                  <Layout>
                    <MovementRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inputs/registry"
              element={
                <ProtectedRoute permission="view animal inputs">
                  <Layout>
                    <InputRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notes/registry"
              element={
                <ProtectedRoute permission="view animal notes">
                  <Layout>
                    <NoteRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazing"
              element={
                <ProtectedRoute permission="view animal grazing">
                  <Layout>
                    <GrazingManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazing/:view"
              element={
                <ProtectedRoute permission="view animal grazing">
                  <Layout>
                    <GrazingManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/drafting"
              element={
                <ProtectedRoute permission="view drafting">
                  <Layout>
                    <Drafting />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/drafting/:view"
              element={
                <ProtectedRoute permission="view drafting">
                  <Layout>
                    <Drafting />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazing-manager"
              element={
                <ProtectedRoute permission="view animal grazing">
                  <Layout>
                    <GrazingManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazing-manager/:view"
              element={
                <ProtectedRoute permission="view animal grazing">
                  <Layout>
                    <GrazingManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/thi-reference"
              element={
                <ProtectedRoute permission="view weather">
                  <Layout>
                    <THIReference />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/report/:token" element={<PublicFieldReport />} />
            <Route path="/w/:token" element={<WorkerSignIn />} />
            <Route
              path="/field-reports/alerts"
              element={
                <ProtectedRoute permission="view alerts">
                  <Layout>
                    <AlertInbox />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-reports/submit"
              element={
                <ProtectedRoute permission="create alerts">
                  <Layout>
                    <SubmitFieldReport />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-reports/mine"
              element={
                <ProtectedRoute permission="create alerts">
                  <Layout>
                    <MyFieldReports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-reports/qr"
              element={
                <ProtectedRoute permission="view alerts">
                  <Layout>
                    <FieldReportQr />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/field-reports" element={<Navigate to="/field-reports/alerts" replace />} />
            <Route path="/alerts/inbox" element={<Navigate to="/field-reports/alerts" replace />} />
            <Route path="/alerts/report" element={<Navigate to="/field-reports/submit" replace />} />
            <Route
              path="/alerts"
              element={<Navigate to="/field-reports/alerts" replace />}
            />
            <Route
              path="/security/*"
              element={
                <ProtectedRoute permission="view security">
                  <Layout>
                    <SecurityBiosecurityDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts/new"
              element={<Navigate to="/field-reports/submit" replace />}
            />
            <Route
              path="/alerts/:id/edit"
              element={<Navigate to="/field-reports/alerts" replace />}
            />
            <Route
              path="/alerts/:id"
              element={
                <ProtectedRoute permission="view alerts">
                  <Layout>
                    <AlertDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animal-dashboard"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <AnimalDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/offspring/registry"
              element={
                <ProtectedRoute permission="view animal offspring">
                  <Layout>
                    <OffspringRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/siblings/registry"
              element={
                <ProtectedRoute permission="view animal siblings">
                  <Layout>
                    <SiblingRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/genealogy/registry"
              element={
                <ProtectedRoute permission="view animal genealogy">
                  <Layout>
                    <GenealogyRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging"
              element={<Navigate to="/tagging/dashboard" replace />}
            />
            <Route
              path="/herd-tags"
              element={<Navigate to="/tagging/dashboard" replace />}
            />
            <Route
              path="/tagging/dashboard"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/system"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingSystemTags />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/custom"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingCustomTags />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/smart"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingSmartTags />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/cohort"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingCohortTags />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/performance"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingPerformanceTags />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagging/rules"
              element={
                <ProtectedRoute permission="view herd tags">
                  <Layout>
                    <TaggingRulesConfig />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/status-dashboard"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <StatusDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/status-dashboard/registry"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <StatusRegistry />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/me"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChangePassword />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/invoices"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OwnerOnly><Invoices /></OwnerOnly>
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Super Admin Users Route */}
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminUsers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/packages"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Packages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Subscriptions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Payments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/upgrade-requests"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UpgradeRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/upgrade"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OwnerOnly><PackageUpgrade /></OwnerOnly>
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Team Management Routes */}
            <Route
              path="/team"
              element={
                <ProtectedRoute permission="view users">
                  <Layout>
                    <TeamUsers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/new"
              element={
                <ProtectedRoute permission="create users">
                  <Layout>
                    <OwnerOnly title="Only the account owner manages team members" subTitle="Ask the owner of this account to add or change team members."><AddEditTeamUser /></OwnerOnly>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/:id/edit"
              element={
                <ProtectedRoute permission="edit users">
                  <Layout>
                    <OwnerOnly title="Only the account owner manages team members" subTitle="Ask the owner of this account to add or change team members."><AddEditTeamUser /></OwnerOnly>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/worker-link"
              element={
                <ProtectedRoute permission="view users">
                  <Layout>
                    <OwnerOnly title="Only the account owner manages the worker QR" subTitle="Ask the owner of this account for the worker sign-in QR."><WorkerLinkQr /></OwnerOnly>
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Roles route commented out - not needed */}
            {/* <Route
              path="/roles"
              element={
                <ProtectedRoute permission="manage roles">
                  <Layout>
                    <Roles />
                  </Layout>
                </ProtectedRoute>
              }
            /> */}
            {/* Contacts Routes */}
            <Route
              path="/contacts"
              element={
                <ProtectedRoute permission="view contacts">
                  <Layout>
                    <Contacts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/new"
              element={
                <ProtectedRoute permission="create contacts">
                  <Layout>
                    <AddEditContacts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/:id/edit"
              element={
                <ProtectedRoute permission="edit contacts">
                  <Layout>
                    <AddEditContacts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Farm Routes */}
            <Route
              path="/locations"
              element={
                <ProtectedRoute permission="view farms">
                  <Layout>
                    <FarmLocations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/farms" element={<Navigate to="/locations?tab=farms" replace />} />
            <Route path="/zones" element={<Navigate to="/locations?tab=zones" replace />} />
            <Route path="/sheds" element={<Navigate to="/locations?tab=sheds" replace />} />
            <Route path="/lots" element={<Navigate to="/locations?tab=lots" replace />} />
            <Route path="/pens" element={<Navigate to="/locations?tab=pens" replace />} />
            <Route
              path="/farms-locations-dashboard"
              element={
                <ProtectedRoute permission="view farms">
                  <Layout>
                    <FarmsLocationsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/farm-map"
              element={
                <ProtectedRoute permission="view farms">
                  <Layout>
                    <FarmMap />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/farms/new"
              element={
                <ProtectedRoute permission="create farms">
                  <Layout>
                    <AddEditFarm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/farms/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditFarm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Department Routes */}
            <Route
              path="/departments"
              element={
                <ProtectedRoute permission="view farms">
                  <Layout>
                    <Departments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/departments/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditDepartment />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/departments/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditDepartment />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Cost Centre Routes */}
            <Route
              path="/cost-centres"
              element={
                <ProtectedRoute permission="view farms">
                  <Layout>
                    <CostCentres />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cost-centres/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditCostCentre />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cost-centres/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditCostCentre />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Zone Routes */}
            <Route
              path="/zones/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditZone />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditZone />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Shed Routes */}
            <Route
              path="/sheds/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditShed />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sheds/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditShed />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Lot Routes */}
            <Route
              path="/lots/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditLot />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lots/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditLot />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Pens Routes */}
            <Route
              path="/pens/new"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditPen />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pens/:id/edit"
              element={
                <ProtectedRoute permission="edit farms">
                  <Layout>
                    <AddEditPen />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Farm finance */}
            <Route
              path="/finance"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <FinanceDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/groups"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <GroupProfitLoss />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/milk"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <MilkMargin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/budget"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <BudgetVsActual />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/overhead"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <OverheadAllocation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/compare"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <ComparePeriods />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/feedlot"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <FeedlotEconomics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/breeding"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <BreedingCosts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/health"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <HealthCosts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/pasture"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <PastureCosts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/lifecycle"
              element={
                <ProtectedRoute permissions={['view transactions']}>
                  <Layout>
                    <LifecycleCost />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Chart of Accounts Routes */}
            <Route
              path="/accounting-dashboard"
              element={
                <ProtectedRoute permissions={['view transactions', 'view expenses', 'view revenues']}>
                  <Layout>
                    <AccountingDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/other-charges"
              element={
                <ProtectedRoute permission="view expenses">
                  <Layout>
                    <OtherCharges />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting-ledgers"
              element={
                <ProtectedRoute permissions={['view transactions', 'view expenses', 'view revenues']}>
                  <Layout>
                    <AccountingLedgers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute permission="view sales">
                  <Layout>
                    <Sales />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart-of-accounts"
              element={
                <ProtectedRoute permission="view transactions">
                  <Layout>
                    <ChartOfAccounts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart-of-accounts/new"
              element={
                <ProtectedRoute permission="create transactions">
                  <Layout>
                    <AddEditChartOfAccount />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart-of-accounts/:id/edit"
              element={
                <ProtectedRoute permission="edit transactions">
                  <Layout>
                    <AddEditChartOfAccount />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Animal Routes */}
            <Route
              path="/activities"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <Activities />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <Animals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/permanent"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <Animals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/new"
              element={
                <ProtectedRoute permission="create animals">
                  <Layout>
                    <AddEditAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/bulk-upload"
              element={
                <ProtectedRoute permission="create animals">
                  <Layout>
                    <BulkUploadAnimals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/treatments/bulk-upload"
              element={
                <ProtectedRoute permission="create animal treatments">
                  <Layout>
                    <BulkUploadTreatments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedings/bulk-upload"
              element={
                <ProtectedRoute permission="create animal feedings">
                  <Layout>
                    <BulkUploadFeedings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inputs/bulk-upload"
              element={
                <ProtectedRoute permission="create animal inputs">
                  <Layout>
                    <BulkUploadInputs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurements/bulk-upload"
              element={
                <ProtectedRoute permission="create animal measurements">
                  <Layout>
                    <BulkUploadMeasurements />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/movements/bulk-upload"
              element={
                <ProtectedRoute permission="create animal movements">
                  <Layout>
                    <BulkUploadMovements />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/yields/bulk-upload"
              element={
                <ProtectedRoute permission="create animal yields">
                  <Layout>
                    <BulkUploadYields />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/real-time-milking"
              element={
                <ProtectedRoute permission="create animal yields">
                  <Layout>
                    <RealTimeMilking />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grazings/bulk-upload"
              element={
                <ProtectedRoute permission="create animal grazing">
                  <Layout>
                    <BulkUploadGrazing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/edit"
              element={
                <ProtectedRoute permission="edit animals">
                  <Layout>
                    <AddEditAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/edit/:tab"
              element={
                <ProtectedRoute permission="edit animals">
                  <Layout>
                    <AddEditAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/preview"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <PreviewAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/preview/:tab"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <PreviewAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/timeline"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <AnimalTimeline />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animals/:id/full-summary"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <FullSummaryAnimal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Other Routes */}
            <Route
              path="/animal-groups"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <AnimalGroups />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animal-groups/new"
              element={
                <ProtectedRoute permission="create animals">
                  <Layout>
                    <AddEditAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animal-groups/:id/edit"
              element={
                <ProtectedRoute permission="edit animals">
                  <Layout>
                    <AddEditAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/animal-groups/:id/preview"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <PreviewAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/feedlots/dashboard"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <FeedlotDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedlots/animals"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <FeedlotAnimals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedlots/new"
              element={
                <ProtectedRoute permission="create animals">
                  <Layout>
                    <AddEditAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedlots/:id/edit"
              element={
                <ProtectedRoute permission="edit animals">
                  <Layout>
                    <AddEditAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedlots/:id/preview"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <PreviewAnimalGroup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedlots"
              element={
                <ProtectedRoute permission="view animals">
                  <Layout>
                    <AnimalGroups />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Feed Formulations Routes */}
            <Route
              path="/feed-formulations"
              element={
                <ProtectedRoute permission="view animal feedings">
                  <Layout>
                    <FeedFormulations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed-formulations/new"
              element={
                <ProtectedRoute permission="create animal feedings">
                  <Layout>
                    <AddEditFeedFormulation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed-formulations/:id/edit"
              element={
                <ProtectedRoute permission="edit animal feedings">
                  <Layout>
                    <AddEditFeedFormulation />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Inventory Routes */}
            <Route
              path="/inventory-dashboard"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <InventoryDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <Inventory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/receive"
              element={
                <ProtectedRoute permission="create inventory">
                  <Layout>
                    <InventoryReceiveStock />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/batches"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <InventoryStockBatches />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/usage"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <InventoryUsageLog />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/warehouses"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <Warehouses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/warehouses/:id"
              element={
                <ProtectedRoute permission="view inventory">
                  <Layout>
                    <WarehouseDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/new"
              element={
                <ProtectedRoute permission="create inventory">
                  <Layout>
                    <AddEditInventory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/:id/edit"
              element={
                <ProtectedRoute permission="edit inventory">
                  <Layout>
                    <AddEditInventory />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute permission="view reports">
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Redirect old agent routes to team */}
            <Route path="/agents" element={<Navigate to="/team" replace />} />
            <Route path="/agents/*" element={<Navigate to="/team" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </AppInitializer>
      </Router>
    </ConfigProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <SpeciesLifecycleProvider>
              <AppDataProvider>
                <AppContent />
              </AppDataProvider>
            </SpeciesLifecycleProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
export default App;
